import { useState } from 'react';
import PageSEO from '@/components/base/PageSEO';
import Breadcrumb from '@/components/base/Breadcrumb';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 火影同人创作指南',
  description: '火影同人创作指南是国内领先的火影忍者同人创作内容平台，致力于为火影同人创作者提供最全面的创作教程、灵感资源和社区交流服务。',
  url: 'https://naruto-fancreations.com/about',
};

const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '火影同人创作指南',
  url: 'https://naruto-fancreations.com',
  description: '火影忍者同人创作综合平台，提供同人小说、插画、CP配对、剧情创作等全方位内容。',
  foundingDate: '2024',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contact@naruto-fancreations.com',
    availableLanguage: ['Chinese'],
  },
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rb4innahb7382ovk6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <PageSEO
        title="关于我们 - 火影忍者同人创作指南"
        description="火影同人创作指南是国内领先的火影忍者同人创作内容平台，致力于为火影同人创作者提供最全面的创作教程、灵感资源和社区交流服务。"
        canonicalUrl="https://naruto-fancreations.com/about"
        keywords="火影同人创作平台,关于我们,火影忍者同人社区"
        jsonLd={{ ...aboutJsonLd, ...orgJsonLd }}
      />
      <main className="pt-20">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <div className="mt-6">
              <section className="mb-16">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                  关于我们
                </h1>
                <div className="prose-content text-foreground-700 leading-relaxed space-y-4">
                  <p>
                    <strong>火影同人创作指南</strong>是国内领先的火影忍者同人创作内容平台。我们汇聚了一群热爱火影忍者的创作者，致力于为火影同人创作者提供最全面的创作教程、灵感资源和社区交流服务。
                  </p>
                  <p>
                    自2024年成立以来，我们始终秉持"让每一个火影迷都能创作出令人惊艳的同人作品"的使命。无论是新手还是资深作者，都能在这里找到属于自己的创作天地。
                  </p>
                </div>
              </section>

              <section className="mb-16">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  我们的使命
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-background-100 rounded-lg p-6 text-center">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                      <i className="ri-lightbulb-line text-2xl"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                      激发创意
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      提供丰富的同人创作灵感与教程，帮助每一位创作者找到自己的创作方向
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-6 text-center">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-4">
                      <i className="ri-group-line text-2xl"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                      连接同好
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      搭建火影同人创作者社区，让志同道合的创作者能够交流、合作、共同成长
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-6 text-center">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                      <i className="ri-award-line text-2xl"></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                      提升品质
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      传播高质量的同人创作理念和技巧，推动火影同人创作水平的整体提升
                    </p>
                  </div>
                </div>
              </section>

              <section className="mb-16">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  用户口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                    <div className="flex items-center gap-1 text-accent-500 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star} className="ri-star-fill text-sm"></i>
                      ))}
                    </div>
                    <p className="text-foreground-700 text-sm leading-relaxed mb-3">
                      "作为一个火影同人创作新手，这个网站给了我巨大的帮助。从写作技巧到角色分析，内容非常全面，强烈推荐！"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-xs font-bold">
                        L
                      </div>
                      <span className="text-foreground-600 text-xs">林同学 · 同人小说创作者</span>
                    </div>
                  </div>
                  <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                    <div className="flex items-center gap-1 text-accent-500 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star} className="ri-star-fill text-sm"></i>
                      ))}
                    </div>
                    <p className="text-foreground-700 text-sm leading-relaxed mb-3">
                      "插画教程的分类非常详细，从入门到进阶都有覆盖。跟着教程练习了两个月，画技进步飞快！"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 text-xs font-bold">
                        W
                      </div>
                      <span className="text-foreground-600 text-xs">王同学 · 同人插画作者</span>
                    </div>
                  </div>
                  <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                    <div className="flex items-center gap-1 text-accent-500 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star} className="ri-star-fill text-sm"></i>
                      ))}
                    </div>
                    <p className="text-foreground-700 text-sm leading-relaxed mb-3">
                      "CP分析的深度让我惊讶，每一篇都干货满满。对于写CP向同人小说的我来说，简直是宝藏网站。"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600 text-xs font-bold">
                        Z
                      </div>
                      <span className="text-foreground-600 text-xs">张同学 · CP同人作者</span>
                    </div>
                  </div>
                  <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                    <div className="flex items-center gap-1 text-accent-500 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star} className="ri-star-fill text-sm"></i>
                      ))}
                    </div>
                    <p className="text-foreground-700 text-sm leading-relaxed mb-3">
                      "平行世界构建那篇文章彻底打开了我的创作思路。现在我已经在连载自己的长篇同人小说了，感谢这个平台！"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-xs font-bold">
                        C
                      </div>
                      <span className="text-foreground-600 text-xs">陈同学 · 长篇同人作者</span>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  联系我们
                </h2>
                <div className="bg-background-100 rounded-lg p-6 md:p-8">
                  {formStatus === 'success' ? (
                    <div className="text-center py-10">
                      <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                        <i className="ri-check-line text-2xl"></i>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                        提交成功！
                      </h3>
                      <p className="text-foreground-600 text-sm">
                        感谢您的留言，我们会在收到后尽快回复。
                      </p>
                    </div>
                  ) : (
                    <form
                      data-readdy-form
                      onSubmit={handleSubmit}
                      className="space-y-5"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            姓名 <span className="text-primary-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            邮箱 <span className="text-primary-500">*</span>
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          主题
                        </label>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                          placeholder="请输入留言主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容 <span className="text-primary-500">*</span>
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          rows={5}
                          maxLength={500}
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                          placeholder="请输入您的留言内容（最多500字）"
                        ></textarea>
                        <p className="text-foreground-400 text-xs mt-1">最多500个字符</p>
                      </div>
                      <div className="honeypot-field">
                        <input
                          type="text"
                          name="phone_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>
                      {formStatus === 'error' && (
                        <div className="p-3 rounded-md bg-primary-50 text-primary-700 text-sm">
                          {formError}
                        </div>
                      )}
                      <button
                        type="submit"
                        className="whitespace-nowrap px-6 py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors duration-200"
                      >
                        发送留言
                      </button>
                    </form>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}